import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
    SewPicture,
    SewWsBreadcrumb,
    SewWsStageHeadlineRotation,
} from '@sew/ui-components-angular/standalone';
import { combineLatest } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { HeaderConfigService, OnDestroyBase } from '../../../../../lib-os-components/src/public-api';
import { environment } from '../../../environments/environment';
import { ConnectedArticleListComponent } from '../../components/connected-article-list/connected-article-list.component';
import { ContentComponent } from '../../components/content/content.component';
import { FsAddSectionComponent } from '../../components/fs/fs-add-section/fs-add-section.component';
import { FsService } from '../../components/fs/fs.service';
import { getBreadcrumbs } from '../../helpers/breadcrumbs.helper';
import { CustomBreakpoint, mediaQueries } from '../../helpers/breakpoint.helper';
import { getImageResolution } from '../../helpers/image.helper';
import { Author } from '../../models/author.model';
import { BlogPost, MetaData } from '../../models/blog-posts.model';
import { BlogLabels } from '../../models/label.model';
import { BlogService } from '../../services/blog/blog.service';
import { Breadcrumbs } from '../../shared/interfaces/breadcrumb.type';
import { RouterService } from '../../shared/services/router.service';

@Component({
    selector: 'blog-author',
    templateUrl: './author.component.html',
    styleUrls: ['./author.component.scss'],
    imports: [
        CommonModule,
        FsAddSectionComponent,
        ContentComponent,
        ConnectedArticleListComponent,
        SewWsStageHeadlineRotation,
        SewPicture,
        SewWsBreadcrumb,
    ],
})
export class AuthorComponent extends OnDestroyBase implements OnInit, OnDestroy {
    public env = environment;

    public breakpoint: CustomBreakpoint = 0;

    public author?: Author;

    public heroPictureURL?: string;

    public allPosts: BlogPost[] = [];

    public labels?: BlogLabels;
    // labels
    public authorLbl?: string;
    public allArticlesLbl?: string;
    public addSectionLbl?: string;
    public labelAllArticles?: string;
    public breadcrumbs: Breadcrumbs = [];

    private firstname?: string;
    private surname?: string;

    public constructor(
        private routerService: RouterService,
        private route: ActivatedRoute,
        private blogService: BlogService,
        private breakpointObserver: BreakpointObserver,
        private metaService: Meta,
        private titleService: Title,
        private cd: ChangeDetectorRef,
        private fsService: FsService,
        private zone: NgZone,
        private headerConfigService: HeaderConfigService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.init();

        if (this.env.preview) {
            this.initOCM();
        }

        this.initBreakpoints();
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.env.preview) {
            this.fsService.clearPreviewElement();
        }
    }

    private init(): void {
        this.firstname = decodeURIComponent(this.route.snapshot.paramMap.get('authorFirstName') ?? '');
        this.surname = decodeURIComponent(this.route.snapshot.paramMap.get('authorLastName') ?? '');
        this.initLabels();

        if (!this.firstname || !this.surname) {
            this.routerService.navigateLandingPage();

            return;
        }

        this.initAuthor(this.firstname, this.surname);

        this.routerService.authorChange.pipe(takeUntil(this.destroySubject)).subscribe((author) => {
            this.author = author;
            this.displayAuthor(this.author);
            this.mapLabels(this.blogService.labels);
        });
    }

    private initAuthor(firstname: string, surname: string): void {
        const languageObs = this.headerConfigService.language.pipe(
            takeUntil(this.destroySubject),
            filter((language) => language.length > 0)
        );
        const countryCodeObs = this.headerConfigService.countryShort.pipe(
            takeUntil(this.destroySubject),
            filter((countryCode) => countryCode.length > 0)
        );

        combineLatest([languageObs, countryCodeObs])
            .pipe(
                switchMap(() =>
                    this.blogService.getAuthorByName(firstname, surname).pipe(takeUntil(this.destroySubject))
                )
            )
            .subscribe((author: Author | null) => {
                if (author === null) {
                    this.zone.run(() => this.routerService.navigateLandingPage());
                } else {
                    this.author = author;
                    this.displayAuthor(this.author);
                }
            });
    }

    private displayAuthor(author: Author): void {
        this.heroPictureURL = getImageResolution(author.hero_picture, this.breakpoint);
        if (this.env.preview) {
            this.fsService.setPreviewElement(author.preview_id);
            // needed for ocm to refresh new/modified content
            this.cd.detectChanges();
        }

        this.updatePageMetaData(author.meta_data);

        const authorName = `${author.firstname} ${author.surname}`;
        this.breadcrumbs = getBreadcrumbs(this.labels, authorName, environment.preview, this.fsService);

        this.blogService
            .getPostsByAuthor(author.author_id)
            .pipe(takeUntil(this.destroySubject))
            .subscribe((response) => {
                this.allPosts = response;
            });
    }

    private updatePageMetaData(metaData: MetaData) {
        this.titleService.setTitle(metaData.pageTitle ?? '');
        this.metaService.updateTag({
            name: 'description',
            content: metaData.description ?? '',
        });
    }

    private initLabels(): void {
        this.blogService.labelsUpdated.pipe(takeUntil(this.destroySubject)).subscribe((labels) => {
            this.mapLabels(labels);
        });
    }

    private mapLabels(labels: BlogLabels): void {
        this.authorLbl = labels.author;
        this.allArticlesLbl = labels.author_articles;
        this.addSectionLbl = labels.create_section;
        this.labelAllArticles = this.allArticlesLbl.replace('$author$', `${this.firstname} ${this.surname}`);
    }

    private initOCM(): void {
        this.fsService.rerenderView.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            this.init();
        });
    }

    private initBreakpoints(): void {
        this.breakpointObserver
            .observe([mediaQueries.mq1, mediaQueries.mq2, mediaQueries.mq3])
            .pipe(takeUntil(this.destroySubject))
            .subscribe((state) => {
                const breakpoints = state.breakpoints;
                let breakpoint: CustomBreakpoint;
                if (breakpoints[mediaQueries.mq3]) {
                    breakpoint = CustomBreakpoint.LARGE;
                } else if (breakpoints[mediaQueries.mq1]) {
                    breakpoint = CustomBreakpoint.SMALL;
                } else if (breakpoints[mediaQueries.mq2]) {
                    breakpoint = CustomBreakpoint.MEDIUM;
                } else {
                    breakpoint = CustomBreakpoint.LARGE;
                }
                this.breakpoint = breakpoint;
            });
    }
}
